/**
 * Names and descriptions of configurations the server created on its own.
 *
 * On first start the backend derives a configuration from the environment and
 * stores it with an English name and description ("Auto-detected Openai",
 * "Automatically created from environment variables for openai"). Those are
 * stored data, so every installation already has them; they are recognised by
 * their exact wording and shown in the language of the interface. Anything an
 * administrator typed does not match and is shown as typed.
 */
import type { TFunction } from 'i18next';

/** How a provider writes its own name; the stored text only has "Openai". */
const PROVIDER_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  azure: 'Azure OpenAI',
  azure_openai: 'Azure OpenAI',
  anthropic: 'Anthropic',
  ollama: 'Ollama',
  huggingface: 'Hugging Face',
  sentence_transformers: 'Sentence Transformers',
  mistral: 'Mistral',
  google: 'Google',
};

const SEEDED_NAME = /^Auto-detected (.+)$/;
const SEEDED_DESCRIPTION = /^Automatically created from environment variables for (.+)$/;
const SEEDED_DEFAULTS_DESCRIPTION = 'Automatically created from environment defaults';

const providerName = (raw: string): string =>
  PROVIDER_NAMES[raw.trim().toLowerCase().replace(/[\s-]+/g, '_')] ?? raw.trim();

export const seededConfigName = (name: string | null | undefined, t: TFunction): string => {
  const match = SEEDED_NAME.exec(name ?? '');
  return match ? t('common.seededConfig.name', { provider: providerName(match[1]) }) : (name ?? '');
};

export const seededConfigDescription = (
  description: string | null | undefined,
  t: TFunction
): string => {
  const text = description ?? '';
  if (text === SEEDED_DEFAULTS_DESCRIPTION) { return t('common.seededConfig.descriptionDefaults'); }
  const match = SEEDED_DESCRIPTION.exec(text);
  return match
    ? t('common.seededConfig.description', { provider: providerName(match[1]) })
    : text;
};
